import {
	CHECK_SESSION_IFRAME_URL,
	KEYCLOAK_CLIENT_ID,
	KEYCLOAK_ORIGIN,
	SESSION_CHECK_INTERVAL_MS,
} from './config';

type TSessionMonitorHandlers = {
	onChanged: () => void;
	onUnsupported: () => void;
};

type TCheckResult = 'unchanged' | 'changed' | 'error';

const isCheckResult = (data: unknown): data is TCheckResult =>
	data === 'unchanged' || data === 'changed' || data === 'error';

let handlers: TSessionMonitorHandlers | null = null;
let iframe: HTMLIFrameElement | null = null;
let iframeReady: Promise<void> | null = null;
let sessionState: string | null = null;
let timerId = 0;
let listening = false;
let disabled = false;

const onMessage = (event: MessageEvent) => {
	if (event.origin !== KEYCLOAK_ORIGIN) return;
	if (!iframe || event.source !== iframe.contentWindow) return;
	if (!isCheckResult(event.data)) return;

	if (event.data === 'error') {
		stop();
		disabled = true;
		handlers?.onUnsupported();
		return;
	}

	if (event.data === 'changed') handlers?.onChanged();
};

const ensureIframe = (): Promise<void> => {
	if (iframeReady) return iframeReady;

	iframeReady = new Promise<void>((resolve, reject) => {
		const element = document.createElement('iframe');
		element.title = 'keycloak session iframe';
		element.setAttribute('aria-hidden', 'true');
		element.setAttribute(
			'sandbox',
			'allow-storage-access-by-user-activation allow-scripts allow-same-origin',
		);
		element.style.cssText =
			'position:absolute;width:0;height:0;border:0;visibility:hidden;left:-9999px;';
		element.onload = () => resolve();
		element.onerror = () => reject(new Error('login-status-iframe failed to load'));
		element.src = CHECK_SESSION_IFRAME_URL;

		iframe = element;
		document.body.appendChild(element);
	});

	return iframeReady;
};

const poll = () => {
	if (!sessionState || !iframe?.contentWindow) return;
	iframe.contentWindow.postMessage(`${KEYCLOAK_CLIENT_ID} ${sessionState}`, KEYCLOAK_ORIGIN);
};

const scheduleNextPoll = () => {
	window.clearTimeout(timerId);
	timerId = window.setTimeout(() => {
		if (document.visibilityState === 'visible') poll();
		scheduleNextPoll();
	}, SESSION_CHECK_INTERVAL_MS);
};

export function stop(): void {
	window.clearTimeout(timerId);
	timerId = 0;
	sessionState = null;
}

export const sessionMonitor = {
	configure: (next: TSessionMonitorHandlers): void => {
		handlers = next;
	},

	start: (nextSessionState: string | null): void => {
		if (disabled || !handlers) return;

		if (!nextSessionState) {
			stop();
			return;
		}

		sessionState = nextSessionState;

		if (!listening) {
			listening = true;
			window.addEventListener('message', onMessage);
		}

		void ensureIframe()
			.then(() => {
				poll();
				scheduleNextPoll();
			})
			.catch(() => {
				disabled = true;
				handlers?.onUnsupported();
			});
	},

	checkNow: (): void => {
		if (disabled) return;
		poll();
	},

	// Sticky, unlike stop(): a later applyTokens() must not quietly restart a monitor we have
	// concluded is unreliable in this environment.
	disable: (): void => {
		disabled = true;
		stop();
	},

	stop,
};
